import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import {
  applyLegalHold,
  getRecycleBin,
  releaseLegalHold,
  requestEntityPurge,
  restoreEntity,
} from '../api/dataLifecycleApi';
import { colorTokens, radiusTokens, typographyTokens } from '../app/designTokens';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { DataTable } from '../components/DataTable';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { TextInput } from '../components/ui/TextInput';

const { semantic } = colorTokens;

const ENTITY_TYPES = [
  'customers',
  'tenants',
  'support_tickets',
  'subscriptions',
  'payments',
  'external_provider_configs',
  'notification_templates',
  'plans',
];

const LIFECYCLE_STATUSES = [
  'active',
  'delete_requested',
  'soft_deleted',
  'restored',
  'purge_scheduled',
  'purged',
  'legal_hold',
];

const rowActionSx = {
  minHeight: 32,
  px: 1,
};

const dangerOutlineSx = {
  ...rowActionSx,
  color: semantic.error,
  border: `1px solid ${semantic.error}`,
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: 'rgba(211, 47, 47, 0.08)',
    borderColor: semantic.error,
    color: semantic.error,
  },
};

const dialogPaperSx = {
  width: 'min(560px, calc(100vw - 48px))',
  maxWidth: 'min(560px, calc(100vw - 48px))',
  maxHeight: 'calc(100vh - 64px)',
  bgcolor: semantic.bg.surface,
  backgroundImage: 'none',
  border: `1px solid ${semantic.border.subtle}`,
  borderRadius: `${radiusTokens.card}px`,
};

function lifecycleTone(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'active' || normalized === 'restored') return 'Success';
  if (normalized === 'delete_requested' || normalized === 'soft_deleted' || normalized === 'purge_scheduled') {
    return 'Warning';
  }
  if (normalized === 'legal_hold') return 'Danger';
  if (normalized === 'purged') return 'Neutral';
  return 'Neutral';
}

function formatDate(value, emptyValue) {
  if (!value) return emptyValue;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

export function RecycleBinPage() {
  const { t } = useTranslation(['dataLifecycle', 'common']);
  const { session } = useAuth();
  const canRestore = hasPermission(session, 'system.data_lifecycle.restore');
  const canPurge = hasPermission(session, 'system.data_lifecycle.purge');
  const canLegalHold = hasPermission(session, 'system.data_lifecycle.legal_hold');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [filters, setFilters] = useState({
    entity_type: '',
    lifecycle_status: '',
    purge_due: '',
    legal_hold: '',
    search: '',
  });
  const [dialog, setDialog] = useState({ open: false, type: null, item: null, reason: '' });

  const emptyValue = t('common:empty_value');
  const recordCount = items.length;

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = { per_page: 100 };
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.lifecycle_status) params.lifecycle_status = filters.lifecycle_status;
      if (filters.purge_due) params.purge_due = true;
      if (filters.legal_hold) params.legal_hold = true;
      if (filters.search) params.search = filters.search;

      const response = await getRecycleBin(params);
      setItems(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const openDialog = useCallback((type, item) => {
    setDialog({ open: true, type, item, reason: '' });
  }, []);

  const closeDialog = () => {
    setDialog({ open: false, type: null, item: null, reason: '' });
  };

  const handleAction = async () => {
    const { type, item, reason } = dialog;
    if (!item) return;

    setMessage('');

    try {
      if (type === 'restore') {
        await restoreEntity(item.entity_type, item.entity_id, { reason });
        setMessage(t('dataLifecycle:recycle_bin.entity_restored'));
      } else if (type === 'purge') {
        await requestEntityPurge(item.entity_type, item.entity_id, { reason });
        setMessage(t('dataLifecycle:recycle_bin.purge_requested'));
      } else if (type === 'legal_hold') {
        await applyLegalHold(item.entity_type, item.entity_id, { reason });
        setMessage(t('dataLifecycle:recycle_bin.legal_hold_applied'));
      } else if (type === 'release_hold') {
        await releaseLegalHold(item.entity_type, item.entity_id, { reason });
        setMessage(t('dataLifecycle:recycle_bin.legal_hold_released'));
      }

      closeDialog();
      loadItems();
    } catch (err) {
      setMessage(err?.response?.data?.message || err.message || t('dataLifecycle:recycle_bin.action_failed'));
    }
  };

  const dialogTitles = {
    restore: t('dataLifecycle:recycle_bin.restore_entity'),
    purge: t('dataLifecycle:recycle_bin.request_purge'),
    legal_hold: t('dataLifecycle:recycle_bin.apply_legal_hold'),
    release_hold: t('dataLifecycle:recycle_bin.release_legal_hold'),
  };

  const columns = useMemo(() => [
    {
      id: 'entity_type',
      label: t('dataLifecycle:recycle_bin.columns.entity'),
      render: (row) => (
        row.entity_type
          ? t(`dataLifecycle:entity_types.${row.entity_type}`, { defaultValue: row.entity_type })
          : emptyValue
      ),
    },
    {
      id: 'display_name',
      label: t('common:name'),
      render: (row) => row.display_name || emptyValue,
    },
    {
      id: 'lifecycle_status',
      label: t('common:status'),
      render: (row) => (
        row.lifecycle_status
          ? (
            <StatusBadge
              status={row.lifecycle_status}
              category="generic"
              apiLabel={t(`dataLifecycle:lifecycle_statuses.${row.lifecycle_status}`, {
                defaultValue: row.lifecycle_status,
              })}
              size="Sm"
              tone={lifecycleTone(row.lifecycle_status)}
            />
          )
          : emptyValue
      ),
    },
    {
      id: 'delete_requested_at',
      label: t('dataLifecycle:recycle_bin.columns.delete_requested'),
      render: (row) => formatDate(row.delete_requested_at, emptyValue),
    },
    {
      id: 'purge_after_at',
      label: t('dataLifecycle:recycle_bin.columns.purge_after'),
      render: (row) => formatDate(row.purge_after_at, emptyValue),
    },
    {
      id: 'is_on_legal_hold',
      label: t('dataLifecycle:recycle_bin.legal_hold'),
      render: (row) => (
        <StatusBadge
          status={row.is_on_legal_hold ? 'yes' : 'no'}
          category="generic"
          apiLabel={row.is_on_legal_hold ? t('common:yes') : t('common:no')}
          size="Sm"
          tone={row.is_on_legal_hold ? 'Warning' : 'Neutral'}
        />
      ),
    },
    {
      id: 'actions',
      label: t('common:actions'),
      align: 'right',
      render: (row) => {
        const hasActions = canRestore
          || (canPurge && row.can_be_purged)
          || canLegalHold;

        if (!hasActions) {
          return emptyValue;
        }

        return (
          <Stack
            direction="column"
            spacing={0.75}
            useFlexGap
            alignItems="flex-end"
            sx={{ gap: '6px', whiteSpace: 'nowrap' }}
          >
            {canRestore && (
              <Button variant="Secondary" onClick={() => openDialog('restore', row)} sx={rowActionSx}>
                {t('common:restore')}
              </Button>
            )}
            {canPurge && row.can_be_purged && (
              <Button variant="Ghost" onClick={() => openDialog('purge', row)} sx={dangerOutlineSx}>
                {t('dataLifecycle:recycle_bin.request_purge_btn')}
              </Button>
            )}
            {canLegalHold && !row.is_on_legal_hold && (
              <Button variant="Ghost" onClick={() => openDialog('legal_hold', row)} sx={rowActionSx}>
                {t('dataLifecycle:recycle_bin.legal_hold')}
              </Button>
            )}
            {canLegalHold && row.is_on_legal_hold && (
              <Button variant="Ghost" onClick={() => openDialog('release_hold', row)} sx={rowActionSx}>
                {t('dataLifecycle:recycle_bin.release_hold')}
              </Button>
            )}
          </Stack>
        );
      },
    },
  ], [t, canRestore, canPurge, canLegalHold, emptyValue, openDialog]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('dataLifecycle:recycle_bin.title')}
        subtitle={t('dataLifecycle:recycle_bin.subtitle')}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('dataLifecycle:recycleBinHelp')}
      </Alert>

      {message && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadItems} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {t('dataLifecycle:recycle_bin.load_failed')}
        </Alert>
      )}

      <FilterBar type="Simple">
        <FilterSelect
          label={t('dataLifecycle:recycle_bin.entity_type')}
          hideLabel
          value={filters.entity_type}
          onChange={(e) => setFilters((prev) => ({ ...prev, entity_type: e.target.value }))}
          minWidth={180}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {ENTITY_TYPES.map((type) => (
            <FilterSelectOption key={type} value={type}>
              {t(`dataLifecycle:entity_types.${type}`, { defaultValue: type })}
            </FilterSelectOption>
          ))}
        </FilterSelect>

        <FilterSelect
          label={t('dataLifecycle:recycle_bin.lifecycle_status')}
          hideLabel
          value={filters.lifecycle_status}
          onChange={(e) => setFilters((prev) => ({ ...prev, lifecycle_status: e.target.value }))}
          minWidth={180}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {LIFECYCLE_STATUSES.map((status) => (
            <FilterSelectOption key={status} value={status}>
              {t(`dataLifecycle:lifecycle_statuses.${status}`, { defaultValue: status })}
            </FilterSelectOption>
          ))}
        </FilterSelect>

        <FilterSelect
          label={t('dataLifecycle:recycle_bin.purge_due')}
          hideLabel
          value={filters.purge_due}
          onChange={(e) => setFilters((prev) => ({ ...prev, purge_due: e.target.value }))}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          <FilterSelectOption value="yes">{t('dataLifecycle:recycle_bin.due_now')}</FilterSelectOption>
        </FilterSelect>

        <FilterSelect
          label={t('dataLifecycle:recycle_bin.legal_hold')}
          hideLabel
          value={filters.legal_hold}
          onChange={(e) => setFilters((prev) => ({ ...prev, legal_hold: e.target.value }))}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          <FilterSelectOption value="yes">{t('dataLifecycle:recycle_bin.on_hold')}</FilterSelectOption>
        </FilterSelect>

        <TextInput
          density="Compact"
          hideLabel
          label={t('common:search')}
          placeholder={t('common:search')}
          value={filters.search}
          onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          sx={{ minWidth: 200, maxWidth: 280 }}
        />
      </FilterBar>

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('dataLifecycle:recycle_bin.items')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('dataLifecycle:recycle_bin.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : !error ? (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={items}
            getRowKey={(row) => `${row.entity_type}-${row.entity_id}`}
            emptyMessage={t('common:no_data_found')}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        ) : null}
      </SectionCard>

      <Dialog
        open={dialog.open}
        onClose={closeDialog}
        maxWidth={false}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.5,
            fontFamily: typographyTokens.fontFamily,
            fontWeight: typographyTokens.fontWeightSemibold,
            fontSize: 16,
            color: semantic.text.primary,
            borderBottom: `1px solid ${semantic.border.subtle}`,
          }}
        >
          {dialogTitles[dialog.type]}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 2.5, overflowY: 'auto' }}>
          <TextInput
            density="Compact"
            label={t('common:reason')}
            value={dialog.reason}
            onChange={(e) => setDialog((prev) => ({ ...prev, reason: e.target.value }))}
            multiline
            minRows={3}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="Ghost" onClick={closeDialog}>
            {t('common:cancel')}
          </Button>
          <Button
            variant={dialog.type === 'purge' ? 'Danger' : 'Accent'}
            onClick={handleAction}
          >
            {t('common:confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
